import React, { useEffect, useRef } from "react";
import PhoneInput, { CountryData } from "react-phone-input-2";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { getFieldConfigByFieldId } from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";
import { FormFieldComponentProps } from "../../../lib/types/types";
import { setRef } from "../../../lib/refs/refs";
import { OptInInputComponent } from "../../FormInputs/OptInInput/OptInInputComponent";
import { FieldLabel } from "../../FieldLabel";

interface PhoneNumberProps {
  explanation?: string | JSX.Element;
  isRequired?: boolean;
  onKeyDown?: Function;
  onCarrierConsentChange?: Function;
  displaySmsConsent?: boolean;
  requireCarrierConsent?: boolean;
  carrierConsent?: boolean;
  internationalSupport?: boolean; // deprecated
  selectedCountryCode?: string | null;
  label?: React.ReactNode;
}

const PhoneNumber = ({
  value,
  isErrored,
  onChange,
  explanation,
  onCarrierConsentChange,
  // eslint-disable-next-line
  intl, // deprecated
  isRequired = false,
  onKeyDown = undefined,
  placeholder = "",
  displaySmsConsent = true,
  // eslint-disable-next-line
  requireCarrierConsent = false, // deprecated
  carrierConsent = false,
  // eslint-disable-next-line
  internationalSupport = false, // deprecated
  selectedCountryCode = "us",
  disabled = false,
  label,
}: FormFieldComponentProps & PhoneNumberProps) => {
  const [dialCode, setDialCode] = React.useState<string>("");

  const getDefaultExplanation = () =>
    !isRequired ? (
      <FormattedHTMLMessage
        id="phoneNumberExplanation"
        defaultMessage="Optional - get text notifications of status updates"
      />
    ) : (
      ""
    );

  const inputRef = useRef<HTMLInputElement>(null);
  const errorHtmlId = "sid-phone-number-error";

  const handleOnChange = (val: string, data: CountryData) => {
    if (!dialCode) {
      setDialCode(data.dialCode);
    }

    // if raw phone number (without dial code) is empty, set empty value for field validation.
    if (!val.slice(dialCode.length)) {
      onChange("");
    } else {
      // react-phone-input-2 returns a string without the + prefix
      onChange(`+${val}`);
    }
  };

  // NOTE: Dependency array omitted to account for use of resetRef(), as useEffect with dep arr
  // will only trigger on initial render leaving the refs empty, this replicates prior version
  // where ref callback would trigger with each re-render
  useEffect(() => {
    if (inputRef.current) {
      setRef("phoneNumber", inputRef.current);
    }
  });

  const isFieldHidden = getFieldConfigByFieldId("phoneNumber")?.hidden;

  if (isFieldHidden) {
    return null;
  }

  return (
    <div className="sid-field sid-phone-number">
      <div className="sid-l-space-top-md" />
      <FieldLabel
        text={label || <FormattedHTMLMessage id="phoneNumber" defaultMessage="Mobile Number" />}
        htmlForLabel="sid-phone-number"
        id="sid-phone-number-label"
        displayClasses={`sid-field__label-with-explanation${
          placeholder && "sid-h-screen-reader-only"
        }`}
        isRequired={isRequired}
      >
        {explanation || getDefaultExplanation()}
      </FieldLabel>

      <PhoneInput
        country={selectedCountryCode.toLowerCase()}
        containerClass="react-tel-input sid-phone-number__wrapper"
        inputClass={`sid-text-input sid-phone-number__field sid-phone-number__field--intl ${
          isRequired ? "sid-text-input--required" : ""
        } sid-l-full-width ${isErrored ? "sid-text-input--error" : ""}`}
        autoFormat={false}
        value={!value ? `${dialCode}` : value}
        onChange={(val: string, data: CountryData) => handleOnChange(val, data)}
        placeholder={placeholder}
        // @ts-ignore
        ref={inputRef}
        inputProps={{
          id: "sid-phone-number",
          name: "sid-phone-number",
          onKeyDown: (e) => (onKeyDown ? onKeyDown(e) : undefined),
          "data-testid": "phone-number-input",
        }}
        aria-labelledby="sid-phone-number-label"
        aria-describedby={isErrored ? errorHtmlId : ""}
        aria-invalid={isErrored}
        countryCodeEditable={false}
        disabled={disabled}
        disableCountryGuess
      />

      {isErrored && (
        <div className="sid-field-error" id={errorHtmlId}>
          <FormattedHTMLMessage
            id="errorId.invalidPhoneNumber"
            defaultMessage="Invalid mobile number"
          />
        </div>
      )}

      {displaySmsConsent && (
        <div className="sid-h-small-text sid-l-space-top-md sid-sms-consent">
          <FormattedHTMLMessage
            id="phoneNumberWarnMessage1"
            defaultMessage="By submitting this form with a mobile number, you consent to receive SMS/text messages for verification purposes. Standard phone carrier text messaging (SMS) and data rates may apply."
          />
        </div>
      )}
      {requireCarrierConsent && (
        <div className="sid-l-position sid-carrier-disclosure-consent">
          <div className="sid-h-small-text sid-l-space-top-md">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- linter fail? */}
            <label htmlFor="sid-opt-in__carrier-consent">
              <OptInInputComponent
                id="carrier-consent"
                ariaDescribedBy={errorHtmlId}
                isErrored={!!isErrored}
                value={carrierConsent}
                onChange={(val) => onCarrierConsentChange && onCarrierConsentChange(val)}
              />
              <div className="sid-field__label sid-checkbox__label">
                <FormattedHTMLMessage
                  id="phoneNumberWarnMessage2"
                  defaultMessage="You authorize your mobile service provider to disclose your mobile number and certain account information to SheerID, solely for identity verification and to avoid fraud."
                />
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export const PhoneNumberComponent = injectIntl(PhoneNumber);
